/**
 * AddSalesRepDialog (a.k.a. "Add Team Member")
 * --------------------------------------------
 * Unified popup used in three places:
 *   - CRM → New Lead/Customer modal → "Add Rep" button
 *   - CRM → row OWNER picker → "+ Add new rep…" option
 *   - Hospitals → detail → "Add new rep" button
 *   - Settings → Users → "Add Sales Rep" + per-row "Promote to Rep"
 *
 * Creates a full USER profile (login account) with a role selector:
 *   Sales Rep / Account Executive / Sales Director / Hub Admin / Super Admin / Accountant / Staff
 *
 * When the role is rep-like (sales_rep), the backend POST /api/users also
 * auto-creates a matching `sales_reps` record and links them, so the new
 * person immediately shows up in the assignment dropdowns. For non-rep roles
 * the user is created but no sales_reps row is added.
 *
 * Pass-through props (kept stable for all existing call sites):
 *   open / onOpenChange / token / prefill / onCreated(rep | userOnlyDoc)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus, KeyRound, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Role catalog — display label, backend `role` value, default permissions,
// and whether this role becomes a sales rep (can be assigned to hospitals).
const ROLES = [
  { label: "Sales Rep",         backend: "sales_rep", perms: ["full"],         is_rep: true,  default_commission: 15 },
  { label: "Account Executive", backend: "sales_rep", perms: ["full"],         is_rep: true,  default_commission: 20 },
  { label: "Sales Director",    backend: "sales_rep", perms: ["full"],         is_rep: true,  default_commission: 25 },
  { label: "Broker",            backend: "sales_rep", perms: ["full"],         is_rep: true,  default_commission: 10 },
  { label: "Accountant",        backend: "hub_admin", perms: ["view_only"],    is_rep: false },
  { label: "Hub Admin",         backend: "hub_admin", perms: ["full"],         is_rep: false },
  { label: "Super Admin",       backend: "super_admin", perms: ["full"],       is_rep: false },
  { label: "Staff",             backend: "staff",     perms: ["maintenance_only"], is_rep: false },
];

const findRole = (label) => ROLES.find((r) => r.label === label) || ROLES[0];

export default function AddSalesRepDialog({ open, onOpenChange, token, onCreated, prefill, currentUserRole }) {
  const isSuperAdmin = currentUserRole === "super_admin";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", role_label: "Sales Rep",
    default_commission_pct: 15,
  });
  const [sendWelcome, setSendWelcome] = useState(true);
  const [titleOnly, setTitleOnly] = useState(false);  // super-admin only: skip login creation
  const [saving, setSaving] = useState(false);

  // Reset / seed the form whenever the dialog opens (or prefill changes)
  useEffect(() => {
    if (!open) return;
    const seedRoleLabel = prefill?.role_label || "Sales Rep";
    const seedRole = findRole(seedRoleLabel);
    setForm({
      name: prefill?.name || "",
      email: prefill?.email || "",
      phone: prefill?.phone || "",
      password: "",
      role_label: seedRoleLabel,
      default_commission_pct: prefill?.default_commission_pct ?? seedRole.default_commission ?? 15,
    });
    setTitleOnly(false);
    setSendWelcome(true);
  }, [open, prefill]);

  // "Title only" is only valid for rep-like roles — force off otherwise
  const role = findRole(form.role_label);
  const titleOnlyEffective = titleOnly && role.is_rep && isSuperAdmin;

  const generatePassword = () => {
    const seg = () => Math.random().toString(36).slice(-4);
    const pwd = `SterileMate${seg()}!${Math.floor(Math.random() * 99)}`;
    setForm((f) => ({ ...f, password: pwd }));
    navigator.clipboard?.writeText(pwd).catch(() => {});
    toast.success("Strong password generated & copied to clipboard");
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required"); return;
    }
    if (!titleOnlyEffective && (!form.password.trim() || form.password.length < 8)) {
      toast.error("Password is required (min 8 characters)"); return;
    }
    setSaving(true);
    try {
      // ─── BRANCH A: Title-only (super-admin) ─────────────────────────────
      // Creates ONLY a sales_reps record so they can be assigned to hospitals
      // for commission tracking. No login account, no welcome email.
      if (titleOnlyEffective) {
        const repRes = await axios.post(
          `${API}/api/admin/sales-reps`,
          {
            name: form.name.trim(),
            email: form.email.trim().toLowerCase(),
            phone: form.phone || null,
            role_label: form.role_label,
            default_commission_pct: Number(form.default_commission_pct) || 15,
            is_active: true,
            notes: "Title-only rep (no login account).",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const repDoc = repRes.data;
        toast.success(`${repDoc.name} added as ${form.role_label} · title only (no login)`);
        onCreated?.(repDoc);
        onOpenChange(false);
        return;
      }

      // ─── BRANCH B: Full user + auto-rep ─────────────────────────────────
      // 1) Create the user account (handles auto sales_reps row when role=sales_rep)
      const userRes = await axios.post(
        `${API}/api/users`,
        {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          password: form.password,
          role: role.backend,
          permissions: role.perms,
          is_active: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const user = userRes.data;

      // 2) If this is a rep role, hydrate the matching sales_rep with role_label
      //    + commission + phone (the backend auto-creates the rep but with defaults)
      let repDoc = null;
      if (role.is_rep) {
        const repsRes = await axios.get(`${API}/api/admin/sales-reps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const reps = repsRes.data?.reps || [];
        repDoc = reps.find((r) => (r.email || "").toLowerCase() === form.email.trim().toLowerCase()) || null;

        if (repDoc) {
          try {
            await axios.patch(
              `${API}/api/admin/sales-reps/${repDoc.id}`,
              {
                role_label: form.role_label,
                phone: form.phone || null,
                default_commission_pct: Number(form.default_commission_pct) || 15,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            repDoc = {
              ...repDoc,
              role_label: form.role_label,
              phone: form.phone || null,
              default_commission_pct: Number(form.default_commission_pct) || 15,
            };
          } catch { /* non-fatal — defaults persist */ }
        }
      }

      toast.success(
        role.is_rep
          ? `${user.name} added as ${form.role_label} · ready to assign hospitals`
          : `${user.name} added as ${form.role_label}`
      );

      // Optionally open Outlook with a pre-filled welcome email that includes
      // the temporary password. Resend SMTP is unverified for sterilemate.com,
      // so we use the reliable `mailto:` handoff.
      if (sendWelcome && user.id) {
        try {
          const wRes = await axios.get(
            `${API}/api/users/${user.id}/welcome-email`,
            {
              params: { password: form.password },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const mailto = wRes.data?.mailto;
          if (mailto) {
            window.location.href = mailto;
            toast.message("Outlook draft opened with login details", {
              description: `Welcome email queued to ${user.email}`,
            });
          }
        } catch {
          toast.warning("User created, but failed to build the welcome email — you can send it later from Settings → Users.");
        }
      }

      onCreated?.(repDoc || user);
      onOpenChange(false);
    } catch (e) {
      // FastAPI returns either a string `detail` (HTTPException) or a list of
      // pydantic validation error objects. Coerce both to a readable string so
      // toast.error() doesn't crash React by trying to render an object child.
      const detail = e.response?.data?.detail;
      let msg = "Failed to add team member";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      else if (e.message) msg = e.message;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="add-rep-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-600" />
            {prefill ? `Promote ${prefill.name || "user"} to team member` : "Add new team member"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Full name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="George Broughton"
              data-testid="add-rep-name"
            />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="george@sterilemate.com"
              data-testid="add-rep-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(281) 555-0123"
              />
            </div>
            <div>
              <Label className="text-xs">Role *</Label>
              <select
                value={form.role_label}
                onChange={(e) => {
                  const next = findRole(e.target.value);
                  setForm((f) => ({
                    ...f,
                    role_label: e.target.value,
                    default_commission_pct: next.is_rep ? next.default_commission : f.default_commission_pct,
                  }));
                }}
                className="w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
                data-testid="add-rep-role"
              >
                {ROLES.map((r) => (
                  <option key={r.label} value={r.label}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          {role.is_rep && (
            <div>
              <Label className="text-xs">Default commission %</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={form.default_commission_pct}
                onChange={(e) => setForm({ ...form, default_commission_pct: e.target.value })}
                data-testid="add-rep-commission"
              />
              <p className="text-[11px] text-slate-500 mt-1">Sales reps can be assigned to hospitals after creation.</p>
            </div>
          )}

          {/* Super-admin shortcut: title only (no login account, no password) */}
          {isSuperAdmin && role.is_rep && (
            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={titleOnly}
                onChange={(e) => setTitleOnly(e.target.checked)}
                className="mt-0.5 accent-amber-600"
                data-testid="add-rep-title-only"
              />
              <span className="text-xs leading-snug">
                <span className="font-semibold text-amber-900">Title only — no login account</span>
                <span className="block text-amber-800/80 mt-0.5">
                  Creates the rep record for hospital assignment and commission tracking, but skips the user/login + welcome email.
                  Useful for adding 1099 brokers or out-of-system reps who don't need portal access.
                </span>
              </span>
            </label>
          )}

          {!titleOnlyEffective && (
            <>
              <div>
                <Label className="text-xs">Temporary password *</Label>
                <div className="flex gap-1.5">
                  <Input
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="min 8 characters"
                    data-testid="add-rep-password"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generatePassword}
                    className="gap-1 whitespace-nowrap"
                    data-testid="add-rep-gen-pass"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Generate
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Share this with the team member — they can change it after first login.</p>
              </div>

              {/* Welcome email auto-action */}
              <label className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/60 p-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWelcome}
                  onChange={(e) => setSendWelcome(e.target.checked)}
                  className="mt-0.5 accent-blue-600"
                  data-testid="add-rep-send-welcome"
                />
                <span className="text-xs leading-snug">
                  <span className="font-semibold text-blue-900 inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Email login credentials right after creation
                  </span>
                  <span className="block text-blue-800/80 mt-0.5">
                    Opens an Outlook draft to <strong>{form.email || "the new team member"}</strong> with login URL, email, temporary password, and a quick role-specific dashboard tour.
                  </span>
                </span>
              </label>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="add-rep-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Save team member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
